import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import { getAccessJwtToken } from "../../store/globalUtilities";
import CmsContext from "../../store/cms-context";

const INSIGHTS_API_BASE =
  "https://fri7xzt2n6.execute-api.eu-west-2.amazonaws.com/SnagCMS/whatsapp/campaign/insights";

// Simple bar component without external dependencies
function SimpleBar({ label, value, max }) {
  const pct = max === 0 ? 0 : (value / max) * 100;
  return (
    <Box mb={1}>
      <Typography variant="caption" gutterBottom>
        {label}: {value.toLocaleString()}
      </Typography>
      <Box
        height={16}
        width="100%"
        bgcolor="#eee"
        borderRadius={4}
        overflow="hidden"
      >
        <Box
          height="100%"
          width={`${pct}%`}
          bgcolor="primary.main"
        />
      </Box>
    </Box>
  );
}

export default function WhatsAppCampaignsRunInsights({
  open,
  campaign,
  onClose,
}) {
  const cmsContext = useContext(CmsContext);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !campaign) return;

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const auth = await getAccessJwtToken();
        const email = cmsContext.current_shop.email;
        const res = await fetch(
          `${INSIGHTS_API_BASE}/${campaign.campaign_id}`,
          {
            method: "GET",
            headers: { Authorization: auth.jwt, email },
          }
        );

        if (!res.ok) {
          console.warn(`Insights fetch returned status ${res.status}`);
          setStats(null);
          return;
        }

        const data = await res.json();
        const isEmptyData =
          !data ||
          (Array.isArray(data) && data.length === 0) ||
          (typeof data === "object" && Object.keys(data).length === 0);

        setStats(isEmptyData ? null : data);
      } catch (e) {
        console.error("Insights fetch error", e);
        setError("Failed to load insights");
        setStats(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, campaign, cmsContext]);

  const formatDuration = (startIso, endIso) => {
    if (!startIso || !endIso) return "";
    const start = new Date(startIso);
    const end = new Date(endIso);
    const diffMs = end - start;
    const diffHrs = Math.floor(diffMs / 1000 / 60 / 60);
    const diffMins = Math.floor((diffMs / 1000 / 60) % 60);
    return `${diffHrs}h ${diffMins}m`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="span" fontWeight="bold">
          {campaign?.campaign_name}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : stats ? (
          <Box>
            <Typography gutterBottom>
              <strong>Queued:</strong> {stats.messages_queued.toLocaleString()} &nbsp;
              <em>Errors:</em> {stats.messages_errored_to_queue.toLocaleString()}
            </Typography>
 
            <Box my={2}>
              {(() => {
                const values = [
                  { label: "Sent", value: stats.messages_sent },
                  { label: "Delivered", value: stats.messages_delivered },
                  { label: "Read", value: stats.messages_read },
                  { label: "Failed", value: stats.messages_failed },
                  { label: "Eventually Delivered", value: stats.messages_eventually_delivered },
                  { label: "Opt-Out", value: stats.messages_opt_out },
                ];
                const maxVal = Math.max(...values.map(v => v.value));
                return values.map(v => (
                  <SimpleBar key={v.label} label={v.label} value={v.value} max={maxVal} />
                ));
              })()}
            </Box>

            <Typography gutterBottom>
              First Sent: {new Date(stats.first_message_sent_at).toLocaleString()}
            </Typography>
            <Typography gutterBottom>
              Last Sent: {new Date(stats.last_message_sent_at).toLocaleString()}
            </Typography>
            <Typography>
              Send Window: {formatDuration(
                stats.first_message_sent_at,
                stats.last_message_sent_at
              )}
            </Typography>
          </Box>
        ) : (
          <Typography>
            Insights not available yet, please check back again later.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}